import axios from 'axios';

interface NewsSource {
  id: string;
  name: string;
  url: string;
  logo: string;
  country: string;
  category: string;
}

interface AggregatedArticle {
  id: string;
  title: string;
  summary: string;
  image: string;
  imageAlt: string;
  source: {
    name: string;
    logo: string;
    logoAlt: string;
    url: string;
  };
  category: string;
  timestamp: string;
  readTime: string;
  isVideo?: boolean;
  country: string;
  platform?: string;
  engagement?: number;
  externalUrl?: string;
}

// NewsAPI.org Integration
export async function fetchNewsAPIArticles(
  country: string = 'us',
  category?: string
): Promise<AggregatedArticle[]> {
  try {
    const apiKey = process.env.NEWS_API_KEY || process.env.NEXT_PUBLIC_NEWS_API_KEY;
    if (!apiKey || apiKey === 'your-newsapi-key-here') {
      console.warn('NewsAPI key not configured');
      return [];
    }

    const countryCode = getCountryCode(country);
    let endpoint = category
      ? `https://newsapi.org/v2/top-headlines?country=${countryCode}&category=${category.toLowerCase()}&apiKey=${apiKey}`
      : `https://newsapi.org/v2/top-headlines?country=${countryCode}&apiKey=${apiKey}`;

    const response = await axios.get(endpoint, {
      timeout: 10000,
    });

    if (response.data.status === 'ok' && response.data.articles) {
      return response.data.articles.slice(0, 20).map((article: any, index: number) => ({
        id: `newsapi_${country}_${index}_${Date.now()}`,
        title: article.title || 'Untitled',
        summary: article.description || article.content?.substring(0, 200) || 'No summary available',
        image: article.urlToImage || '/assets/images/no_image.png',
        imageAlt: `News image for ${article.title}`,
        source: {
          name: article.source?.name || 'NewsAPI',
          logo: '/assets/images/no_image.png',
          logoAlt: `${article.source?.name || 'NewsAPI'} logo`,
          url: article.url || '#',
        },
        category: category || 'General',
        timestamp: getRelativeTime(article.publishedAt),
        readTime: '1 min',
        country: country,
        externalUrl: article.url,
      }));
    }

    return [];
  } catch (error: any) {
    console.error('NewsAPI fetch error:', error.message);
    return [];
  }
}

// YouTube Data API Integration
export async function fetchYouTubeNews(
  country: string = 'US',
  category: string = 'news'
): Promise<AggregatedArticle[]> {
  try {
    const apiKey = process.env.YOUTUBE_API_KEY || process.env.NEXT_PUBLIC_YOUTUBE_API_KEY;
    if (!apiKey || apiKey === 'your-youtube-api-key-here') {
      console.warn('YouTube API key not configured');
      return [];
    }

    const regionCode = getCountryCode(country);
    const searchQuery = `${category} news ${country}`;
    let endpoint = `https://www.googleapis.com/youtube/v3/search?part=snippet&q=${encodeURIComponent(searchQuery)}&type=video&regionCode=${regionCode}&maxResults=10&order=date&key=${apiKey}`;

    const response = await axios.get(endpoint, {
      timeout: 10000,
    });

    if (response.data.items) {
      return response.data.items.map((item: any, index: number) => ({
        id: `youtube_${country}_${index}_${Date.now()}`,
        title: item.snippet.title,
        summary: item.snippet.description.substring(0, 200) || 'Watch this video for more details',
        image: item.snippet.thumbnails?.high?.url || item.snippet.thumbnails?.default?.url || '/assets/images/no_image.png',
        imageAlt: `YouTube video thumbnail for ${item.snippet.title}`,
        source: {
          name: item.snippet.channelTitle || 'YouTube',
          logo: '/assets/images/no_image.png',
          logoAlt: `${item.snippet.channelTitle} logo`,
          url: `https://www.youtube.com/watch?v=${item.id.videoId}`,
        },
        category: category,
        timestamp: getRelativeTime(item.snippet.publishedAt),
        readTime: '2 min',
        isVideo: true,
        platform: 'youtube',
        country: country,
        externalUrl: `https://www.youtube.com/watch?v=${item.id.videoId}`,
      }));
    }

    return [];
  } catch (error: any) {
    console.error('YouTube API fetch error:', error.message);
    return [];
  }
}

// Facebook Graph API Integration (requires page access token)
export async function fetchFacebookNews(
  country: string = 'US'
): Promise<AggregatedArticle[]> {
  try {
    const accessToken = process.env.FACEBOOK_ACCESS_TOKEN || process.env.NEXT_PUBLIC_FACEBOOK_ACCESS_TOKEN;
    if (!accessToken || accessToken === 'your-facebook-token-here') {
      console.warn('Facebook access token not configured');
      return [];
    }

    // Example: Fetch posts from a news page
    const pageId = 'BBCNews'; // Example page ID
    let endpoint = `https://graph.facebook.com/v18.0/${pageId}/posts?fields=id,message,created_time,full_picture,permalink_url&access_token=${accessToken}&limit=10`;

    const response = await axios.get(endpoint, {
      timeout: 10000,
    });

    if (response.data.data) {
      return response.data.data.map((post: any, index: number) => ({
        id: `facebook_${country}_${index}_${Date.now()}`,
        title: post.message?.substring(0, 100) || 'Facebook News Post',
        summary: post.message || 'View this post on Facebook',
        image: post.full_picture || '/assets/images/no_image.png',
        imageAlt: 'Facebook post image',
        source: {
          name: 'Facebook',
          logo: '/assets/images/no_image.png',
          logoAlt: 'Facebook logo',
          url: post.permalink_url || '#',
        },
        category: 'Social',
        timestamp: getRelativeTime(post.created_time),
        readTime: '1 min',
        platform: 'facebook',
        country: country,
        externalUrl: post.permalink_url,
      }));
    }

    return [];
  } catch (error: any) {
    console.error('Facebook API fetch error:', error.message);
    return [];
  }
}

// Instagram Basic Display API Integration
export async function fetchInstagramNews(
  country: string = 'US'
): Promise<AggregatedArticle[]> {
  try {
    const accessToken = process.env.INSTAGRAM_ACCESS_TOKEN || process.env.NEXT_PUBLIC_INSTAGRAM_ACCESS_TOKEN;
    if (!accessToken || accessToken === 'your-instagram-token-here') {
      console.warn('Instagram access token not configured');
      return [];
    }

    let endpoint = `https://graph.instagram.com/me/media?fields=id,caption,media_type,media_url,thumbnail_url,permalink,timestamp&access_token=${accessToken}&limit=10`;

    const response = await axios.get(endpoint, {
      timeout: 10000,
    });

    if (response.data.data) {
      return response.data.data
        .filter((media: any) => media.media_type === 'IMAGE' || media.media_type === 'CAROUSEL_ALBUM')
        .map((media: any, index: number) => ({
          id: `instagram_${country}_${index}_${Date.now()}`,
          title: media.caption?.substring(0, 100) || 'Instagram Post',
          summary: media.caption || 'View this post on Instagram',
          image: media.media_url || media.thumbnail_url || '/assets/images/no_image.png',
          imageAlt: 'Instagram post image',
          source: {
            name: 'Instagram',
            logo: '/assets/images/no_image.png',
            logoAlt: 'Instagram logo',
            url: media.permalink || '#',
          },
          category: 'Social',
          timestamp: getRelativeTime(media.timestamp),
          readTime: '1 min',
          platform: 'instagram',
          country: country,
          externalUrl: media.permalink,
        }));
    }

    return [];
  } catch (error: any) {
    console.error('Instagram API fetch error:', error.message);
    return [];
  }
}

// Twitter/X API v2 Integration
export async function fetchTwitterNews(
  country: string = 'US',
  category: string = 'news'
): Promise<AggregatedArticle[]> {
  try {
    const bearerToken = process.env.TWITTER_BEARER_TOKEN || process.env.NEXT_PUBLIC_TWITTER_BEARER_TOKEN;
    if (!bearerToken || bearerToken === 'your-twitter-bearer-token-here') {
      console.warn('Twitter bearer token not configured');
      return [];
    }

    const query = `${category} ${country} -is:retweet lang:en`;
    let endpoint = `https://api.twitter.com/2/tweets/search/recent?query=${encodeURIComponent(query)}&max_results=10&tweet.fields=created_at,public_metrics&expansions=author_id&user.fields=name,username,profile_image_url`;

    const response = await axios.get(endpoint, {
      headers: {
        Authorization: `Bearer ${bearerToken}`,
      },
      timeout: 10000,
    });

    if (response.data.data && response.data.includes?.users) {
      const users = response.data.includes.users;
      return response.data.data.map((tweet: any, index: number) => {
        const author = users.find((u: any) => u.id === tweet.author_id);
        return {
          id: `twitter_${country}_${index}_${Date.now()}`,
          title: tweet.text.substring(0, 100),
          summary: tweet.text,
          image: author?.profile_image_url || '/assets/images/no_image.png',
          imageAlt: `Tweet by ${author?.name || 'User'}`,
          source: {
            name: author?.name || 'Twitter',
            logo: author?.profile_image_url || '/assets/images/no_image.png',
            logoAlt: `${author?.name || 'Twitter'} profile`,
            url: `https://twitter.com/${author?.username}/status/${tweet.id}`,
          },
          category: category,
          timestamp: getRelativeTime(tweet.created_at),
          readTime: '1 min',
          platform: 'x',
          engagement: tweet.public_metrics?.like_count || 0,
          country: country,
          externalUrl: `https://twitter.com/${author?.username}/status/${tweet.id}`,
        };
      });
    }

    return [];
  } catch (error: any) {
    console.error('Twitter API fetch error:', error.message);
    return [];
  }
}

// Newsdata.io API Integration
export async function fetchNewsdataArticles(
  country: string = 'us',
  category?: string
): Promise<AggregatedArticle[]> {
  try {
    const apiKey = process.env.NEWSDATA_API_KEY || process.env.NEXT_PUBLIC_NEWSDATA_API_KEY;
    if (!apiKey || apiKey === 'your-newsdata-api-key-here') {
      console.warn('Newsdata.io API key not configured');
      return [];
    }

    const countryCode = getCountryCode(country).toLowerCase();
    let endpoint = `https://newsdata.io/api/1/news?apikey=${apiKey}&country=${countryCode}&language=en`;
    
    if (category && category.toLowerCase() !== 'general') {
      endpoint += `&category=${category.toLowerCase()}`;
    }

    const response = await axios.get(endpoint, {
      timeout: 10000,
    });

    if (response.data.status === 'success' && response.data.results) {
      return response.data.results.slice(0, 20).map((article: any, index: number) => ({
        id: `newsdata_${country}_${index}_${Date.now()}`,
        title: article.title || 'Untitled',
        summary: article.description || article.content?.substring(0, 200) || 'No summary available',
        image: article.image_url || '/assets/images/no_image.png',
        imageAlt: `News image for ${article.title}`,
        source: {
          name: article.source_id || 'Newsdata.io',
          logo: '/assets/images/no_image.png',
          logoAlt: `${article.source_id || 'Newsdata.io'} logo`,
          url: article.link || '#',
        },
        category: article.category?.[0] || category || 'General',
        timestamp: getRelativeTime(article.pubDate),
        readTime: '2 min',
        country: country,
        externalUrl: article.link,
      }));
    }

    return [];
  } catch (error: any) {
    console.error('Newsdata.io fetch error:', error.message);
    return [];
  }
}

// Master aggregation function
export async function aggregateNewsFromAllSources(
  country: string = 'US',
  category?: string,
  sources: string[] = ['newsapi', 'youtube', 'newsdata'],
  enableAI: boolean = true
): Promise<AggregatedArticle[]> {
  const promises: Promise<AggregatedArticle[]>[] = [];

  if (sources.includes('newsapi')) {
    promises.push(fetchNewsAPIArticles(country, category));
  }
  if (sources.includes('youtube')) {
    promises.push(fetchYouTubeNews(country, category || 'news'));
  }
  if (sources.includes('facebook')) {
    promises.push(fetchFacebookNews(country));
  }
  if (sources.includes('instagram')) {
    promises.push(fetchInstagramNews(country));
  }
  if (sources.includes('twitter')) {
    promises.push(fetchTwitterNews(country, category || 'news'));
  }
  if (sources.includes('newsdata')) {
    promises.push(fetchNewsdataArticles(country, category));
  }

  const results = await Promise.allSettled(promises);
  const allArticles: AggregatedArticle[] = [];

  results.forEach((result) => {
    if (result.status === 'fulfilled') {
      allArticles.push(...result.value);
    }
  });

  // Sort by timestamp (most recent first)
  return allArticles.sort((a, b) => {
    const timeA = parseRelativeTime(a.timestamp);
    const timeB = parseRelativeTime(b.timestamp);
    return timeB - timeA;
  });
}

// Helper functions
function getCountryCode(country: string): string {
  const countryMap: Record<string, string> = {
    'IN': 'in',
    'US': 'us',
    'GB': 'gb',
    'SG': 'sg',
    'AE': 'ae',
    'NO': 'no',
    'IE': 'ie',
    'CN': 'cn',
    'BR': 'br',
    'JP': 'jp',
    'DE': 'de',
    'FR': 'fr',
    'KR': 'kr',
    'MX': 'mx',
    'IT': 'it',
    'TH': 'th',
    'PK': 'pk',
  };
  return countryMap[country.toUpperCase()] || 'us';
}

function getRelativeTime(dateString: string): string {
  const now = new Date();
  const past = new Date(dateString);
  const diffMs = now.getTime() - past.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 60) return `${diffMins} mins ago`;
  if (diffHours < 24) return `${diffHours} hours ago`;
  return `${diffDays} days ago`;
}

function parseRelativeTime(timeString: string): number {
  const match = timeString.match(/(\d+)\s+(min|hour|day)s?\s+ago/);
  if (!match) return 0;

  const value = parseInt(match[1]);
  const unit = match[2];

  if (unit === 'min') return value;
  if (unit === 'hour') return value * 60;
  if (unit === 'day') return value * 1440;
  return 0;
}